// Tenantra Inc. - MFA Login Verification Component
import { useState } from 'react';
import { 
  Shield, Smartphone, MessageSquare, Key, Loader2, 
  RefreshCw, ArrowLeft, AlertTriangle, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/services/api';

export default function MFALogin({ mfaToken, mfaMethod, phoneLastFour, emailMasked, onSuccess, onCancel }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackup, setUseBackup] = useState(false);
  const [resending, setResending] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await api.post('/mfa/login/verify', {
        mfa_token: mfaToken,
        code: code.replace(/-/g, ''),
        use_backup: useBackup
      });
      
      // Store token and user using correct keys (matching auth.js)
      localStorage.setItem('tenantra_access_token', response.data.token);
      localStorage.setItem('tenantra_user', JSON.stringify(response.data.user));
      
      if (response.data.backup_code_used) {
        toast.warning(`Login successful. You have ${response.data.backup_codes_remaining} backup codes remaining.`);
      } else {
        toast.success('Login successful!');
      }
      
      onSuccess(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setResending(true);
      const endpoint = mfaMethod === 'email' ? '/mfa/login/resend-email' : '/mfa/login/resend-sms';
      await api.post(endpoint, null, {
        params: { mfa_token: mfaToken }
      });
      toast.success(mfaMethod === 'email' ? 'New code sent to your email' : 'New code sent to your phone');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const getMfaDescription = () => {
    if (useBackup) return 'Enter one of your backup recovery codes';
    if (mfaMethod === 'sms') return `Enter the code sent to ***-***-${phoneLastFour}`;
    if (mfaMethod === 'email') return `Enter the code sent to ${emailMasked}`;
    return 'Enter the code from your authenticator app';
  };

  return (
    <div className="space-y-4">
      {/* Header - Compact */}
      <div className="text-center">
        <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center mx-auto mb-3">
          <Shield className="w-5 h-5 text-teal-600" />
        </div>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          {useBackup ? 'Backup Code' : 'Two-Factor Authentication'}
        </h2>
        <p className="text-sm text-slate-500">
          {getMfaDescription()}
        </p>
      </div>

      {/* Method indicator - Compact */}
      {!useBackup && (
        <div className="flex items-center justify-center gap-2 py-2 px-3 bg-slate-50 rounded-lg">
          {mfaMethod === 'sms' ? (
            <>
              <MessageSquare className="w-4 h-4 text-teal-600" />
              <span className="text-sm text-slate-700">SMS Verification</span>
            </>
          ) : mfaMethod === 'email' ? (
            <>
              <Mail className="w-4 h-4 text-teal-600" />
              <span className="text-sm text-slate-700">Email Verification</span>
            </>
          ) : (
            <>
              <Smartphone className="w-4 h-4 text-teal-600" />
              <span className="text-sm text-slate-700">Authenticator App</span>
            </>
          )}
        </div>
      )}

      {/* Verification Form - Compact */}
      <form onSubmit={handleVerify} className="space-y-3">
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">
            {useBackup ? 'Backup Code (XXXX-XXXX)' : '6-Digit Code'}
          </Label>
          <Input
            type="text"
            placeholder={useBackup ? 'XXXX-XXXX' : '000000'}
            value={code}
            onChange={(e) => {
              const val = e.target.value;
              if (useBackup) {
                setCode(val.toUpperCase());
              } else {
                setCode(val.replace(/\D/g, '').slice(0, 6));
              }
            }}
            className={`bg-slate-50 border-slate-200 ${
              useBackup 
                ? 'h-9 text-sm font-mono tracking-wider' 
                : 'h-10 text-center text-lg font-mono tracking-widest'
            }`}
            maxLength={useBackup ? 9 : 6}
            required
          />
        </div>

        <Button
          type="submit"
          disabled={loading || (useBackup ? code.length < 8 : code.length < 6)}
          className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying...
            </span>
          ) : (
            'Verify & Login'
          )}
        </Button>
      </form>

      {/* SMS/Email Resend - Compact */}
      {(mfaMethod === 'sms' || mfaMethod === 'email') && !useBackup && (
        <div className="text-center">
          <button
            onClick={handleResendCode}
            disabled={resending}
            className="text-xs text-teal-600 hover:text-teal-700 inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
            {resending ? 'Sending...' : "Resend code"}
          </button>
        </div>
      )}

      {/* Toggle backup code - Compact */}
      <div className="border-t border-slate-100 pt-3">
        <button
          onClick={() => {
            setUseBackup(!useBackup);
            setCode('');
          }}
          className="w-full text-center text-xs text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1.5"
        >
          <Key className="w-3 h-3" />
          {useBackup ? 'Use authenticator code' : 'Use backup code'}
        </button>
      </div>

      {/* Back button - Compact */}
      <button
        onClick={onCancel}
        className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 py-1"
      >
        <ArrowLeft className="w-3 h-3" />
        Back to login
      </button>

      {/* Help text - Compact */}
      {useBackup && (
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-amber-700 text-xs flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Each backup code can only be used once.</span>
          </p>
        </div>
      )}
    </div>
  );
}
